import type { Vendor } from '@shopgnd/shared';
import { getDataClient } from '@/lib/amplify-client';
import type { VendorPayoutDetails } from '@/lib/payout';

export type VendorPayoutRecord = VendorPayoutDetails & {
  id: string;
  vendorId: string;
  ownerUserId: string;
  payoutsEnabled: boolean;
};

function mapPayout(row: Record<string, unknown>): VendorPayoutRecord {
  return {
    id: String(row.id),
    vendorId: String(row.vendorId),
    ownerUserId: String(row.ownerUserId),
    payoutBankName: row.payoutBankName ? String(row.payoutBankName) : undefined,
    payoutAccountHolderName: row.payoutAccountHolderName
      ? String(row.payoutAccountHolderName)
      : undefined,
    payoutAccountNumber: row.payoutAccountNumber
      ? String(row.payoutAccountNumber)
      : undefined,
    payoutBranchCode: row.payoutBranchCode ? String(row.payoutBranchCode) : undefined,
    payoutsEnabled: Boolean(row.payoutsEnabled),
  };
}

/** Merge public vendor row with payout details (owner/admin fetches only). */
export function mergeVendorWithPayout(
  vendor: Vendor,
  payout?: VendorPayoutRecord | null,
): Vendor {
  if (!payout) return { ...vendor, payoutsEnabled: false };
  return {
    ...vendor,
    payoutBankName: payout.payoutBankName,
    payoutAccountHolderName: payout.payoutAccountHolderName,
    payoutAccountNumber: payout.payoutAccountNumber,
    payoutBranchCode: payout.payoutBranchCode,
    payoutsEnabled: payout.payoutsEnabled,
  };
}

export async function getVendorPayout(vendorId: string): Promise<VendorPayoutRecord | null> {
  try {
    const client = await getDataClient();
    const res = await client.models.VendorPayout.list({
      filter: { vendorId: { eq: vendorId } },
    });
    const row = (res.data ?? [])[0] as Record<string, unknown> | undefined;
    return row ? mapPayout(row) : null;
  } catch (e) {
    console.error('[ShopGND] getVendorPayout failed', e);
    return null;
  }
}

export async function upsertVendorPayout(
  vendorId: string,
  ownerUserId: string,
  details: VendorPayoutDetails & { payoutsEnabled?: boolean },
): Promise<VendorPayoutRecord> {
  const client = await getDataClient();
  const existing = await getVendorPayout(vendorId);
  const payload = {
    vendorId,
    ownerUserId,
    payoutBankName: details.payoutBankName,
    payoutAccountHolderName: details.payoutAccountHolderName,
    payoutAccountNumber: details.payoutAccountNumber,
    payoutBranchCode: details.payoutBranchCode,
    payoutsEnabled: details.payoutsEnabled ?? existing?.payoutsEnabled ?? false,
  };

  if (existing) {
    const res = await client.models.VendorPayout.update({ id: existing.id, ...payload });
    if (!res.data) throw new Error('Could not update payout details');
    return mapPayout(res.data as Record<string, unknown>);
  }

  const res = await client.models.VendorPayout.create(payload);
  if (!res.data) throw new Error('Could not save payout details');
  return mapPayout(res.data as Record<string, unknown>);
}

export async function listVendorPayoutsAdmin(): Promise<VendorPayoutRecord[]> {
  try {
    const client = await getDataClient();
    const res = await client.models.VendorPayout.list();
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapPayout);
  } catch (e) {
    console.error('[ShopGND] listVendorPayoutsAdmin failed', e);
    return [];
  }
}
